import { atom } from "jotai";
import {
  type Edge,
  type NodeChange,
  type EdgeChange,
  type Connection,
  type FinalConnectionState,
  applyNodeChanges,
  applyEdgeChanges,
} from "@xyflow/react";
import type { Getter, Setter } from "jotai";
import { SpotToast } from "@/components/SpotToast";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";
import {
  SINK_ONLY_TYPES,
  SOURCE_ONLY_TYPES,
} from "@/components/SpotflowV2/constants";
import {
  nodesAtom,
  edgesAtom,
  nodeRegistryAtom,
  edgeDragSourceAtom,
  edgeDropTargetNodeIdAtom,
  isEditorReadOnlyAtom,
} from "../atoms";
import {
  markDirty,
  createEdge,
  findNodeDisplayName,
  getInputHandles,
  isCanvasOnlyReactFlowNode,
  resolveTargetHandle,
} from "../helpers";
import {
  growGroupsToFitChildren,
  isGroupReactFlowNode,
  parentGroupOf,
} from "../groups";
import {
  isMergePathsNode,
  mergeInputHandlesFromConfig,
} from "@/components/SpotflowV2/Sidebar/smartRouteBindings";
import { recordTransaction } from "../history/recordTransaction";
import { historyStackAtom } from "../history/atoms";
import {
  historyRuntime,
  type DeleteBuffer,
  type PositionSnapshot,
  type ResizeSnapshot,
} from "../history/runtime";
import type { AtomPatchPair, Transaction } from "../history/types";
import type { Patch } from "immer";
import * as Sentry from "@sentry/nextjs";

/**
 * For routing nodes (those with `output_paths`), an edge MUST originate from
 * a specific named handle — there's no fallback "output". Returns true if
 * the source node is a routing node and no handle was specified.
 */
function isInvalidRoutingConnection(
  sourceNodeId: string | null | undefined,
  sourceHandle: string | null | undefined,
  nodes: NoxusReactflowNodeV2[],
  registry: { type?: string; output_paths?: string[] }[]
): boolean {
  if (sourceHandle) return false;
  const sourceNode = nodes.find((n) => n.id === sourceNodeId);
  if (!sourceNode) return false;
  const def = registry.find((r) => r.type === sourceNode.data?.nodeType);
  return !!(def?.output_paths && def.output_paths.length > 0);
}

/**
 * Input-only nodes render no target handle and output-only nodes render no
 * source handle, so an edge on that side can never be drawn, selected or
 * deleted — but it still persists and still counts as a dependency at run
 * time, which is enough to trip the engine's loop detector.
 *
 * Returns a reason string if the connection would produce such an edge.
 */
export function unattachableEndpointReason(
  sourceNodeId: string | null | undefined,
  targetNodeId: string | null | undefined,
  nodes: NoxusReactflowNodeV2[]
): string | null {
  const target = nodes.find((n) => n.id === targetNodeId);
  if (target && SOURCE_ONLY_TYPES.has(target.data?.nodeType ?? "")) {
    return "Input nodes don't take incoming connections";
  }
  const source = nodes.find((n) => n.id === sourceNodeId);
  if (source && SINK_ONLY_TYPES.has(source.data?.nodeType ?? "")) {
    return "Output nodes don't have outgoing connections";
  }
  return null;
}

/**
 * Shared rules for whether dragging from `(fromNodeId, fromHandleId)` can drop
 * onto `targetNodeId`'s body. Used both by the live drop-target highlight and
 * by the actual connect-on-drop action so the visual feedback matches reality.
 */
export function isValidBodyDropTarget(
  fromNodeId: string,
  fromHandleId: string | null | undefined,
  targetNodeId: string,
  nodes: NoxusReactflowNodeV2[],
  edges: Edge[],
  registry: { type?: string; output_paths?: string[] }[]
): boolean {
  if (fromNodeId === targetNodeId) return false;
  const targetNode = nodes.find((n) => n.id === targetNodeId);
  if (!targetNode || isCanvasOnlyReactFlowNode(targetNode)) return false;
  if (unattachableEndpointReason(fromNodeId, targetNodeId, nodes) !== null) {
    return false;
  }
  if (isInvalidRoutingConnection(fromNodeId, fromHandleId, nodes, registry)) {
    return false;
  }
  const duplicate = edges.some(
    (e) =>
      e.source === fromNodeId &&
      e.target === targetNodeId &&
      (e.sourceHandle ?? null) === (fromHandleId ?? null)
  );
  return !duplicate;
}

/** Dropping an in-flight edge on a group frame opens the picker inside that
 *  group instead of wiring into it — valid whenever the drag itself could
 *  produce an edge (the source may have outgoing edges; a routing drag names
 *  its branch). */
export function isGroupEdgeDropTarget(
  fromNodeId: string,
  fromHandleId: string | null | undefined,
  targetNodeId: string,
  nodes: NoxusReactflowNodeV2[],
  registry: { type?: string; output_paths?: string[] }[]
): boolean {
  const targetNode = nodes.find((n) => n.id === targetNodeId);
  if (!targetNode || !isGroupReactFlowNode(targetNode)) return false;
  if (unattachableEndpointReason(fromNodeId, undefined, nodes) !== null) {
    return false;
  }
  return !isInvalidRoutingConnection(fromNodeId, fromHandleId, nodes, registry);
}

// ── Microtask buffer for delete operations ────────────────────────────────
//
// ReactFlow's `deleteElements({nodes})` fires both `onNodesChange` (removes)
// and `onEdgesChange` (removes for connected edges) as separate setter calls
// in the same task. Without batching we'd get two adjacent history entries
// ("Delete 1 nodes" + "Delete 2 edges"), which Cmd+Z can't roll back as one
// gesture. The buffer collects all `remove`-type change batches that arrive
// within the same microtask and commits them as a single transaction.

function ensureBuffer(get: Getter, set: Setter): DeleteBuffer {
  if (historyRuntime.pendingDelete) return historyRuntime.pendingDelete;
  const buf: DeleteBuffer = {
    nodesBefore: get(nodesAtom),
    edgesBefore: get(edgesAtom),
    removedNodeIds: new Set(),
    removedEdgeIds: new Set(),
  };
  historyRuntime.pendingDelete = buf;
  // Close over `set` from the call that opened the buffer. Subsequent
  // `ensureBuffer` calls during the same microtask short-circuit at the top,
  // so only this first `set` is captured — and it's always the same Setter
  // for a given store.
  queueMicrotask(() => commitDeleteBuffer(get, set));
  return buf;
}

function commitDeleteBuffer(get: Getter, set: Setter): void {
  const buf = historyRuntime.pendingDelete;
  historyRuntime.pendingDelete = null;
  if (!buf) return;
  if (buf.removedNodeIds.size === 0 && buf.removedEdgeIds.size === 0) return;

  // Always mark dirty for the autosave path, even if history recording fails.
  markDirty(set);

  try {
    const nodesAfter = get(nodesAtom);
    const edgesAfter = get(edgesAtom);

    const nodesPair = diffArrayPatches(
      buf.nodesBefore,
      nodesAfter as ReadonlyArray<NoxusReactflowNodeV2>
    );
    const edgesPair = diffArrayPatches(
      buf.edgesBefore,
      edgesAfter as ReadonlyArray<Edge>
    );

    if (!nodesPair && !edgesPair) return;

    const total = buf.removedNodeIds.size + buf.removedEdgeIds.size;
    let label: string;
    if (buf.removedNodeIds.size > 0) {
      if (buf.removedNodeIds.size === 1) {
        const [onlyId] = [...buf.removedNodeIds];
        label = `Delete ${findNodeDisplayName(buf.nodesBefore, onlyId)}`;
      } else {
        label = `Delete ${buf.removedNodeIds.size} nodes`;
      }
    } else {
      label = `Delete ${buf.removedEdgeIds.size} edge${buf.removedEdgeIds.size === 1 ? "" : "s"}`;
    }

    const txn: Transaction = {
      label: total === 1 ? label.replace(/^Delete 1 /, "Delete ") : label,
      nodes: nodesPair,
      edges: edgesPair,
      timestamp: Date.now(),
    };

    set(historyStackAtom, (stack) => ({
      undo: [...stack.undo, txn],
      redo: [],
    }));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "delete-buffer-commit" },
    });
  }
}

// ── Drag lifecycle (ReactFlow's onNodeDragStart / onNodeDragStop) ────────
//
// We use ReactFlow's dedicated drag callbacks rather than scrubbing the
// position-change stream. `nodeDragStartAtom` snapshots pre-drag positions;
// `nodeDragStopAtom` diffs current positions against the snapshot and pushes
// a single "Move N nodes" transaction. Intermediate `onNodeDrag` ticks are
// ignored — the canvas updates via `applyNodeChangesAtom` as before.

export const nodeDragStartAtom = atom(
  null,
  (
    _get,
    _set,
    draggedNodes: ReadonlyArray<{
      id: string;
      position: { x: number; y: number };
    }>
  ) => {
    const snapshot = new Map<string, PositionSnapshot>();
    for (const n of draggedNodes) {
      snapshot.set(n.id, { x: n.position.x, y: n.position.y });
    }
    historyRuntime.dragSnapshot = snapshot;
  }
);

export const nodeDragStopAtom = atom(null, (get, set) => {
  const snapshot = historyRuntime.dragSnapshot;
  historyRuntime.dragSnapshot = null;
  if (!snapshot || snapshot.size === 0) return;

  // Preview / version-view drag is layout-only (discarded on exit) — no history
  // or save queue.
  if (get(isEditorReadOnlyAtom)) return;

  markDirty(set);

  try {
    const currentNodes = get(nodesAtom);
    const moved: Array<{
      id: string;
      oldPos: PositionSnapshot;
      newPos: PositionSnapshot;
    }> = [];
    for (const [id, oldPos] of snapshot) {
      const node = currentNodes.find((n) => n.id === id);
      if (!node) continue;
      if (node.position.x === oldPos.x && node.position.y === oldPos.y)
        continue;
      moved.push({
        id,
        oldPos,
        newPos: { x: node.position.x, y: node.position.y },
      });
    }
    if (moved.length === 0) return;

    const redoPatches: Patch[] = moved.map((m) => ({
      op: "replace",
      path: ["byId", m.id, "position"],
      value: m.newPos,
    }));
    const undoPatches: Patch[] = moved.map((m) => ({
      op: "replace",
      path: ["byId", m.id, "position"],
      value: m.oldPos,
    }));

    const label =
      moved.length === 1
        ? `Move ${findNodeDisplayName(currentNodes, moved[0].id)}`
        : `Move ${moved.length} nodes`;
    const txn: Transaction = {
      label,
      nodes: { redo: redoPatches, undo: undoPatches },
      timestamp: Date.now(),
    };

    set(historyStackAtom, (stack) => ({
      undo: [...stack.undo, txn],
      redo: [],
    }));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "drag-stop" },
    });
  }
});

/**
 * Produce undo/redo patches for an array transition where elements may have
 * been removed. Used by the delete buffer.
 *
 * Why root-level `replace` instead of per-element patches: index-based patches
 * shift under each other when applied (removing index 0 makes index 1 the
 * new index 0, breaking subsequent patches). For the delete case the cleanest
 * scheme is a single full-array snapshot per side — memory cost is two array
 * references (cheap), correctness is bulletproof, and `applyPatches` natively
 * handles a root `replace`.
 */
function diffArrayPatches<T extends { id: string }>(
  before: ReadonlyArray<T>,
  after: ReadonlyArray<T>
): AtomPatchPair | undefined {
  if (before === after) return undefined;
  const beforeIds = new Set(before.map((e) => e.id));
  const afterIds = new Set(after.map((e) => e.id));
  if (
    beforeIds.size === afterIds.size &&
    [...beforeIds].every((id) => afterIds.has(id))
  ) {
    return undefined;
  }

  return {
    redo: [{ op: "replace", path: [], value: after as T[] }],
    undo: [{ op: "replace", path: [], value: before as T[] }],
  };
}

// ── Resize lifecycle ─────────────────────────────────────────────────────
//
// ReactFlow's <NodeResizeControl> doesn't expose the same onStart/onStop
// callbacks at the canvas level the way drag does. Instead it emits a
// stream of `dimensions` changes through `applyNodeChanges`, with
// `resizing: true` during the drag and a final `resizing: false` tick on
// release. We snapshot dimensions on the first `resizing: true` tick per
// node and diff against the post-apply state on the matching `false` tick
// to push one "Resize node(s)" entry per gesture.

function snapshotResize(node: NoxusReactflowNodeV2): ResizeSnapshot {
  return {
    width: node.width,
    height: node.height,
    style: node.style ? { ...node.style } : undefined,
  };
}

function dimensionsEqual(a: ResizeSnapshot, b: ResizeSnapshot): boolean {
  if (a.width !== b.width || a.height !== b.height) return false;
  const aw = a.style?.width;
  const ah = a.style?.height;
  const bw = b.style?.width;
  const bh = b.style?.height;
  return aw === bw && ah === bh;
}

function commitResizeHistory(get: Getter, set: Setter, ids: string[]): void {
  if (get(isEditorReadOnlyAtom)) {
    const snapshots = historyRuntime.resizeSnapshots;
    if (snapshots) {
      for (const id of ids) snapshots.delete(id);
      if (snapshots.size === 0) historyRuntime.resizeSnapshots = null;
    }
    return;
  }
  const snapshots = historyRuntime.resizeSnapshots;
  if (!snapshots || ids.length === 0) return;
  const currentNodes = get(nodesAtom);
  const resized: Array<{
    id: string;
    before: ResizeSnapshot;
    after: ResizeSnapshot;
  }> = [];
  for (const id of ids) {
    const before = snapshots.get(id);
    snapshots.delete(id);
    if (!before) continue;
    const node = currentNodes.find((n) => n.id === id);
    if (!node) continue;
    const after = snapshotResize(node);
    if (dimensionsEqual(before, after)) continue;
    resized.push({ id, before, after });
  }
  if (snapshots.size === 0) historyRuntime.resizeSnapshots = null;
  if (resized.length === 0) return;

  try {
    const redoPatches: Patch[] = [];
    const undoPatches: Patch[] = [];
    for (const r of resized) {
      redoPatches.push({
        op: "replace",
        path: ["byId", r.id, "width"],
        value: r.after.width,
      });
      redoPatches.push({
        op: "replace",
        path: ["byId", r.id, "height"],
        value: r.after.height,
      });
      redoPatches.push({
        op: "replace",
        path: ["byId", r.id, "style"],
        value: r.after.style,
      });
      undoPatches.push({
        op: "replace",
        path: ["byId", r.id, "width"],
        value: r.before.width,
      });
      undoPatches.push({
        op: "replace",
        path: ["byId", r.id, "height"],
        value: r.before.height,
      });
      undoPatches.push({
        op: "replace",
        path: ["byId", r.id, "style"],
        value: r.before.style,
      });
    }

    const label =
      resized.length === 1
        ? `Resize ${findNodeDisplayName(currentNodes, resized[0].id)}`
        : `Resize ${resized.length} nodes`;
    const txn: Transaction = {
      label,
      nodes: { redo: redoPatches, undo: undoPatches },
      timestamp: Date.now(),
    };
    set(historyStackAtom, (stack) => ({
      undo: [...stack.undo, txn],
      redo: [],
    }));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: "editor-v2", component: "history" },
      extra: { phase: "resize-stop" },
    });
  }
}

// ── Apply ReactFlow node changes (position, selection, removal) ───────────

export const applyNodeChangesAtom = atom(
  null,
  (get, set, changes: NodeChange[]) => {
    const effectiveChanges = get(isEditorReadOnlyAtom)
      ? changes.filter((c) => c.type !== "remove")
      : changes;
    if (effectiveChanges.length === 0) return;

    // Capture the pre-removal snapshot BEFORE applying the change so
    // commitDeleteBuffer can diff against the original state. ReactFlow's
    // `deleteElements` fires `onNodesChange` first then `onEdgesChange`, so
    // the very first remove we see during a delete gesture is what defines
    // the baseline — `ensureBuffer` is a no-op on subsequent calls and keeps
    // that baseline.
    const removeChanges = effectiveChanges.filter((c) => c.type === "remove");
    if (removeChanges.length > 0) {
      const buf = ensureBuffer(get, set);
      for (const c of removeChanges) {
        if (c.type === "remove") buf.removedNodeIds.add(c.id);
      }
    }

    // Snapshot pre-resize dimensions on the first `resizing: true` tick per
    // node so commitResizeHistory can diff after the matching `false` tick.
    const resizeStartIds: string[] = [];
    for (const c of effectiveChanges) {
      if (c.type !== "dimensions" || !c.resizing) continue;
      if (!historyRuntime.resizeSnapshots)
        historyRuntime.resizeSnapshots = new Map();
      if (!historyRuntime.resizeSnapshots.has(c.id)) resizeStartIds.push(c.id);
    }
    if (resizeStartIds.length > 0 && historyRuntime.resizeSnapshots) {
      const currentNodes = get(nodesAtom);
      for (const id of resizeStartIds) {
        const node = currentNodes.find((n) => n.id === id);
        if (node) historyRuntime.resizeSnapshots.set(id, snapshotResize(node));
      }
    }

    // Group frames grow around a freshly measured child: the add reserved the
    // default footprint, but the real card can be taller, and React Flow
    // would otherwise clamp it back in.
    const measuredIds = effectiveChanges.flatMap((c) =>
      c.type === "dimensions" && c.dimensions && !c.resizing ? [c.id] : []
    );
    set(nodesAtom, (nds) =>
      growGroupsToFitChildren(
        applyNodeChanges(
          effectiveChanges,
          nds as unknown as Parameters<typeof applyNodeChanges>[1]
        ) as NoxusReactflowNodeV2[],
        measuredIds
      )
    );

    if (removeChanges.length > 0) {
      // markDirty happens once for the whole batch in commitDeleteBuffer.
      return;
    }

    // Drag-end position changes (`dragging:false`) still mark the editor
    // dirty so autosave runs; the *history* entry comes from the dedicated
    // `nodeDragStopAtom` wired to ReactFlow's `onNodeDragStop` callback.
    const hasFinalPositionChange = effectiveChanges.some(
      (c) => c.type === "position" && !c.dragging
    );
    const finalResizeIds: string[] = [];
    for (const c of effectiveChanges) {
      if (c.type !== "dimensions" || c.resizing) continue;
      if (historyRuntime.resizeSnapshots?.has(c.id)) finalResizeIds.push(c.id);
    }
    // Dimension changes only mark dirty when they end a user resize (tracked
    // in resizeSnapshots). React Flow also emits `dimensions` when it first
    // measures nodes after a load or preview-exit restore — marking dirty
    // there turns merely opening a flow into an unattended autosave.
    if (
      (hasFinalPositionChange || finalResizeIds.length > 0) &&
      !get(isEditorReadOnlyAtom)
    ) {
      markDirty(set);
    }
    if (finalResizeIds.length > 0) {
      commitResizeHistory(get, set, finalResizeIds);
    }
  }
);

// ── Apply ReactFlow edge changes (removal, selection) ─────────────────────

export const applyEdgeChangesAtom = atom(
  null,
  (get, set, changes: EdgeChange<Edge>[]) => {
    const effectiveChanges = get(isEditorReadOnlyAtom)
      ? changes.filter((c) => c.type !== "remove")
      : changes;
    if (effectiveChanges.length === 0) return;

    // Capture pre-removal snapshot BEFORE applying. See applyNodeChangesAtom
    // for the rationale.
    const removeChanges = effectiveChanges.filter((c) => c.type === "remove");
    if (removeChanges.length > 0) {
      const buf = ensureBuffer(get, set);
      for (const c of removeChanges) {
        if (c.type === "remove") buf.removedEdgeIds.add(c.id);
      }
    }

    set(edgesAtom, (eds) => applyEdgeChanges(effectiveChanges, eds));

    if (removeChanges.length > 0) {
      markDirty(set);
    }
  }
);

// ── Connect two nodes (handle-to-handle) ──────────────────────────────────

/** The target node's named input handles (`input_paths`): config-derived for
 * dynamic-input nodes (Merge Paths rows), then per-instance data, then the
 * registry fallback — same precedence the node card renders with. */
function namedInputHandlesFor(get: Getter, targetId: string): string[] {
  const node = get(nodesAtom).find((n) => n.id === targetId);
  if (!node) return [];
  if (isMergePathsNode(node.data?.nodeType)) {
    return mergeInputHandlesFromConfig(node.data?.nodeConfig).map((h) => h.id);
  }
  const dataHandles = node.data?.inputHandles ?? [];
  if (dataHandles.length > 0) return dataHandles;
  const registryEntry = get(nodeRegistryAtom).find(
    (r) => r.type === node.data?.nodeType
  );
  return registryEntry ? getInputHandles(registryEntry) : [];
}

function takenTargetHandles(edges: Edge[], targetId: string): Set<string> {
  return new Set(
    edges
      .filter((e) => e.target === targetId)
      .map((e) => e.targetHandle ?? "input")
  );
}

export const connectAtom = atom(null, (get, set, connection: Connection) => {
  if (get(isEditorReadOnlyAtom)) return;
  const { source, target, sourceHandle } = connection;
  // A drop on the node body (or a stale generic handle) resolves onto the
  // target's named input handle when it has input_paths — those nodes render
  // no "input" handle, so the edge would otherwise never attach (RF #008).
  const targetHandle = resolveTargetHandle({
    requested: connection.targetHandle,
    namedHandles: namedInputHandlesFor(get, target ?? ""),
    takenHandles: takenTargetHandles(get(edgesAtom), target ?? ""),
  });

  // Prevent self-loops
  if (source === target) {
    SpotToast.error("Cannot connect a node to itself");
    return;
  }

  const unattachable = unattachableEndpointReason(
    source,
    target,
    get(nodesAtom)
  );
  if (unattachable !== null) {
    SpotToast.error(unattachable);
    return;
  }

  // Routing nodes require a specific source handle (when_true / when_false / …)
  if (
    isInvalidRoutingConnection(
      source,
      sourceHandle,
      get(nodesAtom),
      get(nodeRegistryAtom)
    )
  ) {
    SpotToast.error("Drag from a specific output branch on this node");
    return;
  }

  const edges = get(edgesAtom);

  // Prevent duplicate edges on the same (source, sourceHandle, target, targetHandle) tuple
  const duplicate = edges.find(
    (e) =>
      e.source === source &&
      e.target === target &&
      (e.sourceHandle ?? null) === (sourceHandle ?? null) &&
      (e.targetHandle ?? null) === (targetHandle ?? null)
  );
  if (duplicate) return;

  const newEdge = createEdge({
    source: source ?? "",
    target: target ?? "",
    sourceHandle: sourceHandle ?? undefined,
    // Pass the user-specified target handle through. createEdge falls back to
    // "input" when omitted (legacy single-input nodes), but for nodes with
    // named target handles (MergePathsNode's path_a/b, …) we MUST preserve the
    // handle id or the edge won't render.
    targetHandle: targetHandle ?? undefined,
  });

  const nodes = get(nodesAtom);
  const sourceName = findNodeDisplayName(nodes, source ?? "");
  const targetName = findNodeDisplayName(nodes, target ?? "");
  recordTransaction(
    get,
    set,
    `Connect ${sourceName} → ${targetName}`,
    ({ edges }) => {
      edges.push(newEdge as (typeof edges)[number]);
    }
  );
  markDirty(set);
});

// ── Connect drag lifecycle ────────────────────────────────────────────────

/** Called from React Flow's `onConnectStart` — records the source for live
 *  drop-target tracking and clears any stale highlight. */
export const connectStartAtom = atom(
  null,
  (_get, set, params: { nodeId: string | null; handleId: string | null }) => {
    if (!params.nodeId) return;
    set(edgeDragSourceAtom, {
      nodeId: params.nodeId,
      handleId: params.handleId,
    });
    set(edgeDropTargetNodeIdAtom, null);
  }
);

/** Called from a global mousemove during an active edge drag — sets the node
 *  under the cursor as the live drop target if it passes all guards. */
export const updateEdgeDropTargetAtom = atom(
  null,
  (get, set, candidateNodeId: string | null) => {
    const drag = get(edgeDragSourceAtom);
    if (!drag) {
      if (get(edgeDropTargetNodeIdAtom) !== null) {
        set(edgeDropTargetNodeIdAtom, null);
      }
      return;
    }
    if (!candidateNodeId) {
      if (get(edgeDropTargetNodeIdAtom) !== null) {
        set(edgeDropTargetNodeIdAtom, null);
      }
      return;
    }
    const nodes = get(nodesAtom);
    const registry = get(nodeRegistryAtom);
    const valid =
      isValidBodyDropTarget(
        drag.nodeId,
        drag.handleId,
        candidateNodeId,
        nodes,
        get(edgesAtom),
        registry
      ) ||
      isGroupEdgeDropTarget(
        drag.nodeId,
        drag.handleId,
        candidateNodeId,
        nodes,
        registry
      );
    const next = valid ? candidateNodeId : null;
    if (get(edgeDropTargetNodeIdAtom) !== next) {
      set(edgeDropTargetNodeIdAtom, next);
    }
  }
);

// ── Connect on drop (drag edge onto node body) ───────────────────────────

export const connectOnDropAtom = atom(
  null,
  (
    get,
    set,
    args: {
      event: MouseEvent | TouchEvent;
      connectionState: FinalConnectionState;
      onEmptyDrop?: (info: {
        fromNodeId: string;
        fromHandleId: string | null;
        fromHandleType: "source" | "target";
        clientPos: { x: number; y: number };
        /** Group whose frame the edge was dropped in, if any. */
        parentGroupId?: string;
      }) => void;
    }
  ) => {
    if (get(isEditorReadOnlyAtom)) {
      set(edgeDragSourceAtom, null);
      set(edgeDropTargetNodeIdAtom, null);
      return;
    }
    const { event, connectionState, onEmptyDrop } = args;

    // Always clear drag state at the end of the gesture.
    const cleanup = () => {
      set(edgeDragSourceAtom, null);
      set(edgeDropTargetNodeIdAtom, null);
    };

    // Skip if the connection was already handled as a valid handle-to-handle connection
    if (connectionState.isValid) {
      cleanup();
      return;
    }
    if (!connectionState.fromNode) {
      cleanup();
      return;
    }

    const fromNodeId = connectionState.fromNode.id;
    const fromHandle = connectionState.fromHandle;

    const clientPos =
      "changedTouches" in event
        ? {
            x: event.changedTouches[0].clientX,
            y: event.changedTouches[0].clientY,
          }
        : {
            x: (event as MouseEvent).clientX,
            y: (event as MouseEvent).clientY,
          };

    // React Flow only sets toNode when dropping near a handle.
    // When dropping on the node body, manually detect which node is under the cursor.
    let toNodeId = connectionState.toNode?.id;

    if (!toNodeId) {
      // Walk up from the element under the cursor to find a React Flow node wrapper
      const el = document.elementFromPoint(clientPos.x, clientPos.y);
      const nodeEl = el?.closest<HTMLElement>("[data-id].react-flow__node");
      if (nodeEl) {
        toNodeId = nodeEl.getAttribute("data-id") ?? undefined;
      }
    }

    // Surface a toast for routing nodes used without a handle — distinguishes
    // a user mistake from the silent no-op cases (self-loop, source-only, dup).
    // Applied before the empty-drop branch so an invalid routing drag never
    // opens the picker.
    if (
      isInvalidRoutingConnection(
        fromNodeId,
        fromHandle?.id,
        get(nodesAtom),
        get(nodeRegistryAtom)
      )
    ) {
      SpotToast.error("Drag from a specific output branch on this node");
      cleanup();
      return;
    }

    // A group frame or a sticky note under the cursor is canvas, not a node to
    // wire into: treat the drop as an empty-canvas drop and let the caller
    // open the picker — inside the group the drop landed in.
    const toNode = get(nodesAtom).find((n) => n.id === toNodeId);
    if (!toNodeId || (toNode && isCanvasOnlyReactFlowNode(toNode))) {
      const dropGroup =
        toNode && isGroupReactFlowNode(toNode)
          ? toNode
          : toNode
            ? parentGroupOf(toNode.id, get(nodesAtom))
            : undefined;
      onEmptyDrop?.({
        fromNodeId,
        fromHandleId: fromHandle?.id ?? null,
        // React Flow handles default to "source" if unset; targets are explicit.
        fromHandleType: fromHandle?.type === "target" ? "target" : "source",
        clientPos,
        parentGroupId: dropGroup?.id,
      });
      cleanup();
      return;
    }

    const valid = isValidBodyDropTarget(
      fromNodeId,
      fromHandle?.id,
      toNodeId,
      get(nodesAtom),
      get(edgesAtom),
      get(nodeRegistryAtom)
    );
    if (!valid) {
      cleanup();
      return;
    }

    // Nodes with named input handles (input_paths) have no "input" handle —
    // fill the first free path instead.
    const targetHandle = resolveTargetHandle({
      requested: undefined,
      namedHandles: namedInputHandlesFor(get, toNodeId),
      takenHandles: takenTargetHandles(get(edgesAtom), toNodeId),
    });

    const newEdge = createEdge({
      source: fromNodeId,
      target: toNodeId,
      sourceHandle: fromHandle?.id ?? undefined,
      targetHandle,
    });
    const nodes = get(nodesAtom);
    const sourceName = findNodeDisplayName(nodes, fromNodeId);
    const targetName = findNodeDisplayName(nodes, toNodeId);
    recordTransaction(
      get,
      set,
      `Connect ${sourceName} → ${targetName}`,
      ({ edges }) => {
        edges.push(newEdge as (typeof edges)[number]);
      }
    );
    markDirty(set);
    cleanup();
  }
);
